<div class="flex w-full gap-x-4">
  <div class="mb-6 flex flex-1 flex-col gap-4 md:w-1/2">
    <!-- Account header -->
    <section
      aria-labelledby="billing-account-heading"
      class="bg-white px-4 py-4 sm:px-6 sm:py-6"
    >
      <div
        class="lg2:flex-row lg2:items-center lg2:justify-between flex flex-col gap-4"
      >
        <div class="min-w-0 flex-1">
          <h2
            id="billing-account-heading"
            class="font-montserrat text-lg font-bold text-black"
          >
            {{ currentUser()?.Account?.Name ?? '—' }}
          </h2>

          @if (currentUser()?.Account?.BillingAddress) {
            <p class="font-lato text-sm text-black">
              {{ currentUser()!.Account!.BillingAddress!.street }}
              @if (currentUser()!.Account!.BillingAddress!.city) {
                , {{ currentUser()!.Account!.BillingAddress!.city }}
              }
              @if (currentUser()!.Account!.BillingAddress!.state) {
                , {{ currentUser()!.Account!.BillingAddress!.state }}
              }
              @if (currentUser()!.Account!.BillingAddress!.postalCode) {
                {{ currentUser()!.Account!.BillingAddress!.postalCode }}
              }
            </p>
          }

          <p class="font-lato text-brand-muted mt-1 text-sm">
            Account #{{ currentUser()?.Account?.BAN__c ?? '—' }}
            @if (summary()?.billingCycle) {
              <span aria-hidden="true" class="mx-1">·</span>
              Billing cycle: {{ formatBillingCycle(summary()!.billingCycle) }}
            }
            @if (preferences()?.paymentTerms) {
              <span aria-hidden="true" class="mx-1">·</span>
              Terms: {{ preferences()!.paymentTerms }}
            }
            @if (preferences()?.paymentMethod) {
              <span aria-hidden="true" class="mx-1">·</span>
              {{ preferences()!.paymentMethod }}
            }
            @if (preferences()?.paperlessEnabled != null) {
              <span aria-hidden="true" class="mx-1">·</span>
              {{
                preferences()!.paperlessEnabled
                  ? 'Paperless billing'
                  : 'Paper billing'
              }}
            }
          </p>

          @if (!summary()) {
            <!-- Skeleton loaders while summary is loading -->
            <div
              class="mt-6 grid animate-pulse grid-cols-1 gap-4 lg:grid-cols-3"
              aria-hidden="true"
            >
              <div>
                <div class="mb-2 h-4 w-32 rounded bg-gray-200"></div>
                <div class="mb-1 h-8 w-24 rounded bg-gray-200"></div>
                <div class="h-4 w-20 rounded bg-gray-200"></div>
              </div>
              <div>
                <div class="mb-2 h-4 w-32 rounded bg-gray-200"></div>
                <div class="mb-1 h-8 w-24 rounded bg-gray-200"></div>
                <div class="h-4 w-20 rounded bg-gray-200"></div>
              </div>
              <div>
                <div class="mb-2 h-4 w-32 rounded bg-gray-200"></div>
                <div class="mb-1 h-8 w-24 rounded bg-gray-200"></div>
                <div class="h-4 w-20 rounded bg-gray-200"></div>
              </div>
            </div>
          } @else {
            <dl class="mt-6 grid grid-cols-1 gap-2 xl:grid-cols-2">
              <!-- Account Balance -->
              <!-- TODO: restore when Salesforce is fixed -->
              <!-- <div>
                <dt
                  class="font-lato flex items-center gap-1 text-xs font-bold tracking-[0.5px] uppercase"
                >
                  Account Balance
                  <mat-icon
                    class="flex! h-4! w-4! cursor-default items-center! justify-center! text-base!"
                    matTooltip="This is your current balance for this account, inclusive of all credits, payments, charges, and balance forward."
                    matTooltipPosition="above"
                    aria-hidden="true"
                    >info_outline</mat-icon
                  >
                  <span class="sr-only"
                    >This is your current balance for this account, inclusive of
                    all credits, payments, charges, and balance forward.</span
                  >
                </dt>
                @if (summary()?.currentBalance != null) {
                  <dd
                    class="font-montserrat text-2xl font-bold"
                    [class.text-green-700]="summary()!.isCreditBalance"
                    [class.text-red-600]="
                      !summary()!.isCreditBalance &&
                      summary()!.currentBalance! > 0
                    "
                    [class.text-black]="
                      !summary()!.isCreditBalance &&
                      summary()!.currentBalance! <= 0
                    "
                  >
                    {{
                      summary()!.currentBalance!
                        | currency: 'USD' : 'symbol' : '1.2-2'
                    }}
                    @if (summary()!.isCreditBalance) {
                      <span class="text-base font-semibold">CR</span>
                    }
                    @if (
                      !summary()!.isCreditBalance &&
                      summary()!.currentBalance! > 0
                    ) {
                      <span class="sr-only">balance owed</span>
                    }
                    @if (
                      summary()!.balanceStatus &&
                      summary()!.balanceStatus !== 'Current' &&
                      summary()!.balanceStatus !== 'Unknown'
                    ) {
                      <span
                        class="font-lato mt-1 block rounded bg-red-50 px-2 py-0.5 text-xs font-semibold tracking-wide text-red-700 uppercase"
                      >
                        {{ summary()!.balanceStatus }}
                      </span>
                    }
                    @if (summary()!.pastDueAmount != null) {
                      <p class="font-lato mt-0.5 text-xs text-red-600">
                        {{
                          summary()!.pastDueAmount!
                            | currency: 'USD' : 'symbol' : '1.2-2'
                        }}
                        past due
                      </p>
                    }
                  </dd>
                } @else {
                  <dd class="font-lato text-sm text-gray-500">
                    Billing information will appear here once available
                  </dd>
                }
              </div> -->

              <!-- Last Payment Received -->
              <div>
                <dt
                  class="font-lato text-xs font-bold tracking-[0.5px] uppercase"
                >
                  Last Payment Received
                </dt>
                @if (summary()?.lastPayment?.amount != null) {
                  <dd class="font-montserrat text-2xl font-bold text-black">
                    {{
                      summary()!.lastPayment!.amount
                        | currency: 'USD' : 'symbol' : '1.2-2'
                    }}
                  </dd>
                } @else {
                  <dd class="font-lato text-sm text-gray-600">
                    No payment history
                  </dd>
                }
                @if (summary()?.lastPayment?.date) {
                  <p class="font-lato text-brand-muted mt-1 text-sm">
                    <time [attr.datetime]="summary()!.lastPayment!.date">
                      {{ summary()!.lastPayment!.date | date: 'MMMM d, yyyy' }}
                    </time>
                  </p>
                }
              </div>

              <!-- Current Invoice -->
              <div>
                <dt
                  class="font-lato text-xs font-bold tracking-[0.5px] uppercase"
                >
                  Current Invoice
                </dt>
                @if (summary()?.statementBalance != null) {
                  <dd class="font-montserrat text-2xl font-bold text-black">
                    {{
                      summary()!.statementBalance!
                        | currency: 'USD' : 'symbol' : '1.2-2'
                    }}
                  </dd>
                } @else {
                  <dd class="font-lato text-sm text-gray-600">
                    No invoice yet
                  </dd>
                }
                @if (summary()?.dueDate) {
                  <p class="font-lato text-brand-muted mt-1 text-sm">
                    Due
                    <time [attr.datetime]="summary()!.dueDate!">{{
                      summary()!.dueDate! | date: 'MMMM d, yyyy'
                    }}</time>
                  </p>
                }
              </div>
            </dl>
          }
        </div>

        <div
          class="mt-2 grid gap-y-2 sm:mt-0 sm:w-72"
          role="group"
          aria-label="Billing actions"
        >
          @if (summary()?.hasPaymentLink) {
            <button
              type="button"
              class="btn-primary flex w-full items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-2"
              [disabled]="!summary()?.hasPaymentLink || isPaymentLinkLoading()"
              [attr.aria-busy]="isPaymentLinkLoading() ? 'true' : null"
              [attr.aria-label]="
                isPaymentLinkLoading()
                  ? 'Processing payment, please wait'
                  : 'Make a payment'
              "
              [matTooltip]="
                !summary()?.hasPaymentLink
                  ? 'Payment will be available once your bill is generated'
                  : ''
              "
              matTooltipPosition="above"
              (click)="onPayClick()"
            >
              @if (isPaymentLinkLoading()) {
                <mat-spinner
                  [diameter]="18"
                  class="text-white!"
                  aria-hidden="true"
                ></mat-spinner>
              }
              Make Payment
            </button>
          }

          <button
            type="button"
            class="btn-secondary flex w-full items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-2"
            [attr.aria-disabled]="
              !summary()?.latestInvoiceContentVersionId ? 'true' : null
            "
            [disabled]="isViewBillLoading()"
            [attr.aria-busy]="isViewBillLoading() ? 'true' : null"
            [attr.aria-label]="
              isViewBillLoading()
                ? 'Opening bill PDF, please wait'
                : 'View current bill as PDF'
            "
            [matTooltip]="
              !summary()?.latestInvoiceContentVersionId
                ? 'No bill available yet'
                : ''
            "
            matTooltipPosition="above"
            [ngClass]="{
              'cursor-not-allowed opacity-50':
                !summary()?.latestInvoiceContentVersionId &&
                !isViewBillLoading(),
              'cursor-wait': isViewBillLoading(),
            }"
            (click)="onViewBill()"
          >
            @if (isViewBillLoading()) {
              <mat-spinner [diameter]="18" aria-hidden="true"></mat-spinner>
            }
            View Bill (PDF)
          </button>
        </div>
      </div>
    </section>

    <!-- Tabbed card -->
    <div class="overflow-hidden bg-white shadow-sm">
      <!-- Tab headers -->
      <div
        class="divide-gray-150 grid grid-cols-2 divide-x border-b border-gray-500"
        role="tablist"
        aria-label="Billing sections"
      >
        <button
          role="tab"
          [attr.aria-selected]="activeTab() === 'invoices'"
          aria-controls="billing-invoices-panel"
          id="billing-invoices-tab"
          class="relative cursor-pointer px-8 pt-7 pb-5 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-inset"
          [ngClass]="activeTab() === 'invoices' ? 'bg-white' : 'bg-[#f7f7f7]'"
          (click)="activeTab.set('invoices')"
          (keydown)="onTabKeydown($event, 'invoices')"
        >
          <span
            class="font-montserrat mb-2.75 block text-lg font-bold tracking-wide transition-colors"
            [class.text-blue-700]="activeTab() === 'invoices'"
            [class.text-black]="activeTab() !== 'invoices'"
          >
            Bills
          </span>
          <p class="font-lato text-body mb-2 text-[13px] tracking-widest">
            Includes statements and bill summaries
          </p>
          <div
            class="absolute inset-x-[15%] bottom-0 h-1 rounded-t-sm transition-opacity"
            [class.bg-blue-700]="activeTab() === 'invoices'"
            [class.opacity-0]="activeTab() !== 'invoices'"
            aria-hidden="true"
          ></div>
        </button>

        <button
          role="tab"
          [attr.aria-selected]="activeTab() === 'transactions'"
          aria-controls="billing-transactions-panel"
          id="billing-transactions-tab"
          class="relative cursor-pointer px-8 pt-7 pb-5 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-inset"
          [ngClass]="
            activeTab() === 'transactions' ? 'bg-white' : 'bg-[#f7f7f7]'
          "
          (click)="activeTab.set('transactions')"
          (keydown)="onTabKeydown($event, 'transactions')"
        >
          <span
            class="font-montserrat mb-2.75 block text-lg font-bold tracking-wide transition-colors"
            [class.text-blue-700]="activeTab() === 'transactions'"
            [class.text-black]="activeTab() !== 'transactions'"
          >
            Transactions
          </span>
          <p class="font-lato text-body mb-2 text-[13px] tracking-widest">
            Includes upcoming and past payments
          </p>
          <div
            class="absolute inset-x-[15%] bottom-0 h-1 rounded-t-sm transition-opacity"
            [class.bg-blue-700]="activeTab() === 'transactions'"
            [class.opacity-0]="activeTab() !== 'transactions'"
            aria-hidden="true"
          ></div>
        </button>
      </div>

      <!-- Tab panels -->
      <div
        id="billing-invoices-panel"
        role="tabpanel"
        aria-labelledby="billing-invoices-tab"
        class="pt-6"
        [hidden]="activeTab() !== 'invoices'"
      >
        <app-invoice-list
          [invoices]="invoices()"
          [isInvoicesLoading]="isInvoicesLoading()"
          [selectedInvoiceId]="selectedInvoice()?.Id"
          (invoiceSelected)="onInvoiceSelected($event)"
        ></app-invoice-list>
      </div>

      <div
        id="billing-transactions-panel"
        role="tabpanel"
        aria-labelledby="billing-transactions-tab"
        class="pt-6"
        [hidden]="activeTab() !== 'transactions'"
      >
        <app-transaction-list
          [payments]="payments()"
          [isPaymentsLoading]="isPaymentsLoading()"
        ></app-transaction-list>
      </div>
    </div>

    @if (invoices()?.length > 0) {
      <app-help-cta [originPage]="'Billing'"></app-help-cta>
    }
  </div>

  <!-- PDF Viewer Panel -->
  <div
    class="hidden bg-white shadow-sm md:flex md:w-1/2 md:flex-col md:overflow-hidden"
  >
    @if (selectedInvoice() && pdfUrl()) {
      <div class="flex items-center justify-between px-6 py-4">
        <div class="min-w-0 flex-1">
          <h2 class="font-montserrat font-semibold text-black">
            Invoice -
            {{
              selectedInvoice()?.Transaction_Date__c
                ? (selectedInvoice()!.Transaction_Date__c
                  | date: 'MMMM d, yyyy')
                : ''
            }}
          </h2>
        </div>
      </div>

      @if (isLoadingPdf()) {
        <div
          class="flex flex-1 animate-pulse flex-col gap-3 overflow-hidden bg-gray-50 p-6"
          role="status"
          aria-label="Loading PDF"
        >
          <div class="h-6 w-3/4 rounded bg-gray-200"></div>
          <div class="space-y-2">
            <div class="h-4 w-full rounded bg-gray-200"></div>
            <div class="h-4 w-5/6 rounded bg-gray-200"></div>
            <div class="h-4 w-4/5 rounded bg-gray-200"></div>
          </div>
          <div class="mt-4 space-y-2">
            <div class="h-4 w-full rounded bg-gray-200"></div>
            <div class="h-4 w-5/6 rounded bg-gray-200"></div>
          </div>
          <div class="mt-6 h-40 rounded bg-gray-200"></div>
        </div>
      } @else {
        <iframe
          [src]="safePdfUrl()"
          class="flex-1"
          role="document"
          [attr.aria-label]="
            'PDF preview for invoice dated ' +
            (selectedInvoice()!.Transaction_Date__c | date: 'MMMM d, yyyy')
          "
        ></iframe>
      }
    } @else {
      <div
        class="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-8"
        role="status"
        aria-live="polite"
      >
        <mat-icon
          fontIcon="description"
          class="text-gray-300"
          [style.font-size.px]="48"
          [style.width.px]="48"
          [style.height.px]="48"
          aria-hidden="true"
        ></mat-icon>
        <div class="text-center">
          @if (invoices()?.length > 0) {
            <h3 class="font-montserrat mb-1 text-base font-semibold text-black">
              Select an invoice to preview
            </h3>
            <p class="font-lato text-sm text-gray-600">
              Choose an invoice from the Bills tab to view its PDF here.
            </p>
          } @else {
            <h3 class="font-montserrat mb-1 text-base font-semibold text-black">
              No invoices to preview
            </h3>
            <p class="font-lato text-sm text-gray-600">
              Invoices will appear here once they're available.
            </p>
          }
        </div>
      </div>
    }
  </div>
</div>
